import { useCallback, useEffect, useState } from 'react'
import { api, type CommentThread } from '../api'
import { Icon } from './Icon'

/**
 * The conversation about one page.
 *
 * Threads are anchored to WORDS, so each one shows the text it was made on
 * and says plainly when those words have changed — a comment pointing
 * confidently at the wrong paragraph is worse than one admitting it lost its
 * place. Resolved threads are kept and collapsed rather than deleted: a
 * settled question is a record of a decision, and the next person to ask it
 * deserves to find the answer.
 */
export function CommentsPanel({
  path,
  branch,
  readOnly,
  mayComment,
  pending,
  onPendingUsed,
  onChanged,
}: {
  path: string
  branch: string | null
  /** Editorial actions — settling a thread is a decision about the docs. */
  readOnly: boolean
  /**
   * Saying something is not editing.
   *
   * Spec 7 gives a viewer "read accessible private content and optionally
   * comment", and this panel took one flag for both — so the person invited
   * precisely to give an opinion was shown a conversation with no way into it.
   */
  mayComment: boolean
  /** A selection the editor handed over, waiting for its first comment. */
  pending: { quote: string; prefix: string; suffix: string } | null
  onPendingUsed: () => void
  onChanged: () => void
}) {
  const [threads, setThreads] = useState<CommentThread[] | null>(null)
  const [draft, setDraft] = useState('')
  const [replyTo, setReplyTo] = useState<string | null>(null)
  const [replyBody, setReplyBody] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showSettled, setShowSettled] = useState(false)

  const load = useCallback(async () => {
    try {
      const result = await api.comments(path, branch)
      setThreads(result.comments)
      setError(null)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    }
  }, [path, branch])
  useEffect(() => void load(), [load])

  const say = async () => {
    if (!draft.trim()) return
    setBusy(true)
    try {
      await api.addComment({
        path,
        branch,
        body: draft,
        ...(pending
          ? { quote: pending.quote, prefix: pending.prefix, suffix: pending.suffix }
          : {}),
      })
      setDraft('')
      onPendingUsed()
      await load()
      onChanged()
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  const reply = async (parentId: string) => {
    if (!replyBody.trim()) return
    setBusy(true)
    try {
      await api.addComment({ path, branch, body: replyBody, parentId })
      setReplyBody('')
      setReplyTo(null)
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  const settle = async (id: string, resolved: boolean) => {
    setBusy(true)
    try {
      await api.resolveComment(id, resolved)
      await load()
      onChanged()
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  if (threads === null) return null
  const open = threads.filter((t) => !t.resolvedAt)
  const settled = threads.filter((t) => t.resolvedAt)

  return (
    <section className="comments">
      <div className="label labelrow">
        Comments
        {settled.length > 0 && (
          <button className="btn btn--ghost btn--sm" onClick={() => setShowSettled((s) => !s)}>
            {showSettled ? 'Hide' : 'Show'} {settled.length} settled
          </button>
        )}
      </div>

      {error && (
        <p className="screen__error">
          <Icon name="alert" size={13} /> {error}
        </p>
      )}

      {open.length === 0 && !pending && (
        <p className="nothing">
          Nothing yet. Select some words in the editor and press Comment to ask about them, or leave
          a note about the whole page below.
        </p>
      )}

      <ul className="comments__list">
        {[...open, ...(showSettled ? settled : [])].map((thread) => (
          <li
            key={thread.id}
            className={`comments__thread${thread.resolvedAt ? ' comments__thread--settled' : ''}`}
          >
            {thread.quote && (
              <div
                className={`comments__quote${thread.anchor.state !== 'anchored' ? ' comments__quote--lost' : ''}`}
              >
                <span className="truncate">“{thread.quote}”</span>
                {thread.anchor.state === 'orphaned' && (
                  <span className="comments__note">these words have changed</span>
                )}
                {thread.anchor.state === 'ambiguous' && (
                  <span className="comments__note">appears more than once now</span>
                )}
              </div>
            )}
            <div className="comments__who">
              <b>{thread.authorName}</b>
              {thread.resolvedAt && (
                <span className="badge badge--quiet">settled by {thread.resolvedBy}</span>
              )}
            </div>
            <p className="comments__body">{thread.body}</p>

            {thread.replies.map((r) => (
              <div key={r.id} className="comments__reply">
                <div className="comments__who">
                  <b>{r.authorName}</b>
                </div>
                <p className="comments__body">{r.body}</p>
              </div>
            ))}

            {mayComment && (
              <div className="comments__actions">
                {replyTo === thread.id ? (
                  <>
                    <input
                      className="input"
                      placeholder="Reply…"
                      value={replyBody}
                      disabled={busy}
                      autoFocus
                      onChange={(e) => setReplyBody(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') void reply(thread.id)
                        if (e.key === 'Escape') setReplyTo(null)
                      }}
                    />
                    <button
                      className="btn btn--sm btn--outline"
                      disabled={busy || !replyBody.trim()}
                      onClick={() => void reply(thread.id)}
                    >
                      Send
                    </button>
                  </>
                ) : (
                  <button
                    className="btn btn--ghost btn--sm"
                    disabled={busy}
                    onClick={() => setReplyTo(thread.id)}
                  >
                    Reply
                  </button>
                )}
                {/* Settling is editorial: it says the question about the
                    documentation is answered. Replying is not. */}
                {!readOnly && (
                  <button
                    className="btn btn--ghost btn--sm"
                    disabled={busy}
                    onClick={() => void settle(thread.id, !thread.resolvedAt)}
                  >
                    {thread.resolvedAt ? 'Reopen' : 'Mark settled'}
                  </button>
                )}
              </div>
            )}
          </li>
        ))}
      </ul>

      {mayComment && (
        <div className="comments__new">
          {pending && (
            <div className="comments__quote">
              <span className="truncate">“{pending.quote}”</span>
              <button className="btn btn--ghost btn--sm" onClick={onPendingUsed}>
                <Icon name="close" size={11} label="Drop the selection" />
              </button>
            </div>
          )}
          <input
            className="input"
            placeholder={pending ? 'What about this?' : 'A note about this page…'}
            value={draft}
            disabled={busy}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void say()
            }}
          />
          <button
            className="btn btn--sm"
            disabled={busy || !draft.trim()}
            onClick={() => void say()}
          >
            {busy ? <Icon name="spinner" size={13} /> : <Icon name="callout" size={13} />}
            Comment
          </button>
        </div>
      )}
    </section>
  )
}
